import { useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, Fehler, hole } from "./klient";
import { mitMappe } from "./mappe";
import { endeNachtragen, sendeOderEinreihen, useWarteschlange } from "./warteschlange";
import type { Wartender } from "./warteschlange";
import { tagKurz as tagKurzImBetrieb, uhrImBetrieb } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Einsatzzeit — Daten, Haken und die laufende Uhr (AD6, 0108).

   **Eine eigene Datei, nicht drei Stellen in `typen.ts`, `routen.ts` und
   `haken.ts`.** Die Einsatzzeit hat ihre eigenen Wege (`/handy/einsatz`,
   `/zeiten`) und ihre eigenen Masken — Handy, Brett, Auftrag. Was sie
   braucht, steht beisammen; angeglichen wird trotzdem hier und nur hier:
   In den Masken steht kein Feldname aus der Datenbank.

   **Regel 9.** Jede Frage hier ist an einen Einsatz gebunden — die
   eigene Belegung heute, eine Belegung im Brett, ein Auftrag. Eine
   Summe je Mensch über Einsätze hinweg gibt es nicht, auch nicht als
   Nebenprodukt.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const txt = (wert: unknown): string =>
  wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert);
const zahl = (wert: unknown): number => {
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : 0;
};

/* ══════════════ Formen ══════════════ */

/** Ein Einsatz der angemeldeten Person heute — `GET /handy/einsatz`. */
export type Einsatz = {
  belegungId: string;
  vorgang: string;
  kunde: string;
  ort: string;
  ortschaft: string;
  hinweis: string;
  zufahrt: string;
  /** Die Position, wie sie verkauft wurde: „Kran MKG 401". */
  position: string;
  /** Die Kennungen der Geräte an der Position: „KR-01". */
  geraete: string;
  /** Der laufende Eintrag von heute — `null`, wenn nichts läuft. */
  laeuft: { id: string; seit: string } | null;
  /** Die laufende Anfahrt von heute (0118) — `null`, wenn niemand unterwegs ist. */
  unterwegs: { id: string; seit: string } | null;
  /** Was heute an dieser Belegung schon abgeschlossen ist. */
  sekundenHeute: number;
  /** Wie viele abgeschlossene Einträge heute dastehen — bestätigen braucht einen. */
  eintraegeHeute: number;
  /** Der Kunde hat die Stunden dieses Tages bestätigt (0117) — leer: steht noch aus. */
  quittiertAm: string | null;
  quittiertVon: string;
};

/** Ein offener Eintrag von einem früheren Tag — vergessen auszustempeln. */
export type OffenerEintrag = {
  id: string;
  belegungId: string;
  seit: string;
  vorgang: string;
  kunde: string;
};

export type MeinTag = { heute: Einsatz[]; vortagOffen: OffenerEintrag[] };

/** Ein Eintrag, wie Blatt und Auftrag ihn lesen — `GET /zeiten`. */
export type Zeiteintrag = {
  id: string;
  belegungId: string;
  beginn: string;
  /** Leer, solange die Zeit läuft. */
  ende: string | null;
  /** Der Kalendertag in Berlin, `JJJJ-MM-TT`. */
  tag: string;
  vortagOffen: boolean;
  person: string;
  erfasstVon: string;
  nachgetragen: boolean;
  korrekturen: { am: string; grund: string }[];
  positionId: string;
  position: string;
};

/** Eine Anfahrt (0118) — erfasst, nicht abgerechnet. */
export type Anfahrt = { id: string; beginn: string; ende: string | null; tag: string };

export type BelegungsZeiten = { erfassbar: boolean; eintraege: Zeiteintrag[]; anfahrten: Anfahrt[] };

/** Ein Einsatztag eines Auftrags — eine Position, ein Kalendertag (0108/0117). */
export type Einsatztag = {
  id: string;
  tag: string;
  positionId: string;
  position: string;
  sekunden: number;
  gestempelt: boolean;
  laeuft: boolean;
  /** Der Tag liegt hinter uns — dann fällt eine fehlende Quittung auf. */
  vorbei: boolean;
  quittiertAm: string | null;
  quittiertVon: string;
  /** Wer die Quittung aufgenommen hat — Fahrer oder Disposition. */
  quittiertDurch: string;
  /** Die Zählerstände des Tages (0119) — Nachweis, nicht Rechnung. */
  zaehlerBeginn: number | null;
  zaehlerEnde: number | null;
  /** Das Unterschriftsbild als Ablagepfad — über `GET /ablage/url` (0121). */
  unterschriftPfad: string;
  /** Aus der Warteschlange bestätigt: `signed_at` ist die Uhr des Geräts. */
  offlineBestaetigt: boolean;
  /** Was der Kunde bestätigt hat, eingefroren (0121). */
  bestaetigtSekunden: number | null;
  bestaetigteZaehlerBeginn: number | null;
  bestaetigteZaehlerEnde: number | null;
  bestaetigteFotos: number | null;
  /** Nach der Bestätigung wurde eine Zeit geändert — gelbe Ampel. */
  nachBestaetigungGeaendertAm: string | null;
};

/** Eine Mietposition mit Bediener — oder eine, die einen bräuchte. */
export type Einsatzposten = {
  positionId: string;
  position: string;
  /** `nur_mit_person`, `ohne_person`, `wahlweise` — aufgelöst am Maschinentyp. */
  bedienungspflicht: string;
  bediener: { belegungId: string; personId: string; name: string }[];
};

/**
 * Was am Vorgang fehlt: ein Bediener, ein Fahrer zum Haken „mit Bediener"
 * (18.09.2026), eine Quittung — oder eine Zeit, die nach der Quittung geändert
 * wurde.
 */
export type OffenerPunkt = {
  art: "bediener_fehlt" | "fahrer_fehlt" | "nicht_quittiert" | "nach_bestaetigung_geaendert"
    | "frueher_frei";
  positionId: string;
  position: string;
  tag: string | null;
  /** Warum der Fahrer den Tag ohne Bestätigung geschlossen hat (18.09.2026). */
  grund: string | null;
  /** Bei `frueher_frei` (B1): wer früher frei ist, ab wann, und bis wann verkauft war. */
  ressource: "maschine" | "person" | null;
  name: string;
  freiAb: string | null;
  verkauftBis: string | null;
};

/**
 * Das Einsatzblatt eines Auftrags — `GET /zeiten?vorgang=`.
 *
 * Luca, 17.09.2026: *„Das ist ein separates Modul unter Zeiten. Das ist ja
 * dumm. Da ist ja ein Vorgang."* Deshalb kommt hier nicht nur die Stundenliste
 * heraus, sondern alles, was der Vorgang über seine Einsätze weiß.
 */
export type Vorgangszeiten = {
  zeilen: Zeiteintrag[];
  /** Die Anfahrten (0118) — erfasst, nicht abgerechnet, nicht in `zeilen`. */
  anfahrten: (Anfahrt & { positionId: string; person: string; fahrzeug: string })[];
  einsaetze: Einsatztag[];
  posten: Einsatzposten[];
  offenePunkte: OffenerPunkt[];
};

function alsEintrag(z: Roh): Zeiteintrag {
  return {
    id: txt(z["id"]),
    belegungId: txt(z["belegung_id"]),
    beginn: txt(z["starts_at"]),
    ende: z["ends_at"] ? txt(z["ends_at"]) : null,
    tag: txt(z["tag"]),
    vortagOffen: z["vortag_offen"] === true,
    person: txt(z["person"]),
    erfasstVon: txt(z["erfasst_von"]),
    nachgetragen: z["nachgetragen"] === true,
    korrekturen: alsListe(z["corrections"]).map((k) => ({ am: txt(k["am"]), grund: txt(k["grund"]) })),
    positionId: txt(z["position_id"]),
    position: txt(z["position"]),
  };
}

/* ══════════════ Die Wege ══════════════ */

export async function meinTag(): Promise<MeinTag> {
  if (ATTRAPPE) return { heute: [], vortagOffen: [] };
  const roh = await hole<Roh>("/handy/einsatz");
  return {
    heute: alsListe(roh["heute"]).map((z) => ({
      belegungId: txt(z["belegung_id"]),
      vorgang: txt(z["vorgang"]),
      kunde: txt(z["kunde"]),
      ort: txt(z["ort"]),
      ortschaft: txt(z["ortschaft"]),
      hinweis: txt(z["hinweis"]),
      zufahrt: txt(z["zufahrt"]),
      position: txt(z["position"]),
      geraete: txt(z["geraete"]),
      laeuft: z["laeuft_id"] ? { id: txt(z["laeuft_id"]), seit: txt(z["laeuft_seit"]) } : null,
      unterwegs: z["unterwegs_id"] ? { id: txt(z["unterwegs_id"]), seit: txt(z["unterwegs_seit"]) } : null,
      sekundenHeute: zahl(z["sekunden_heute"]),
      eintraegeHeute: zahl(z["eintraege_heute"]),
      quittiertAm: txt(z["quittiert_am"]) || null,
      quittiertVon: txt(z["quittiert_von"]),
    })),
    vortagOffen: alsListe(roh["vortag_offen"]).map((z) => ({
      id: txt(z["id"]),
      belegungId: txt(z["belegung_id"]),
      seit: txt(z["seit"]),
      vorgang: txt(z["vorgang"]),
      kunde: txt(z["kunde"]),
    })),
  };
}

export async function zeitenDerBelegung(belegungId: string): Promise<BelegungsZeiten> {
  if (ATTRAPPE) return { erfassbar: false, eintraege: [], anfahrten: [] };
  const roh = await hole<Roh>(`/zeiten?belegung=${encodeURIComponent(belegungId)}`);
  return {
    erfassbar: roh["erfassbar"] === true,
    eintraege: alsListe(roh["eintraege"]).map(alsEintrag),
    anfahrten: alsListe(roh["anfahrten"]).map((f) => ({
      id: txt(f["id"]),
      beginn: txt(f["starts_at"]),
      ende: f["ends_at"] ? txt(f["ends_at"]) : null,
      tag: txt(f["tag"]),
    })),
  };
}

const LEERES_BLATT: Vorgangszeiten = { zeilen: [], anfahrten: [], einsaetze: [], posten: [], offenePunkte: [] };

const zahlOderNull = (w: unknown): number | null => {
  if (w === null || w === undefined || w === "") return null;
  const n = typeof w === "number" ? w : Number(w);
  return Number.isFinite(n) ? n : null;
};

export async function zeitenDesVorgangs(nr: string): Promise<Vorgangszeiten> {
  if (ATTRAPPE) return LEERES_BLATT;
  const roh = await hole<Roh>(`/zeiten?vorgang=${encodeURIComponent(nr)}`);
  return {
    zeilen: alsListe(roh["zeilen"]).map(alsEintrag),
    anfahrten: alsListe(roh["anfahrten"]).map((f) => ({
      id: txt(f["id"]),
      beginn: txt(f["starts_at"]),
      ende: f["ends_at"] ? txt(f["ends_at"]) : null,
      tag: txt(f["tag"]),
      positionId: txt(f["position_id"]),
      person: txt(f["person"]),
      fahrzeug: txt(f["fahrzeug"]),
    })),
    einsaetze: alsListe(roh["einsaetze"]).map((e): Einsatztag => ({
      id: txt(e["id"]),
      tag: txt(e["tag"]),
      positionId: txt(e["position_id"]),
      position: txt(e["position"]),
      sekunden: zahl(e["sekunden"]),
      gestempelt: e["gestempelt"] === true,
      laeuft: e["laeuft"] === true,
      vorbei: e["vorbei"] === true,
      quittiertAm: txt(e["quittiert_am"]) || null,
      quittiertVon: txt(e["quittiert_von"]),
      quittiertDurch: txt(e["quittiert_durch"]),
      zaehlerBeginn: zahlOderNull(e["zaehler_beginn"]),
      zaehlerEnde: zahlOderNull(e["zaehler_ende"]),
      unterschriftPfad: txt(e["unterschrift_pfad"]),
      offlineBestaetigt: e["offline_bestaetigt"] === true,
      bestaetigtSekunden: zahlOderNull(e["bestaetigt_sekunden"]),
      bestaetigteZaehlerBeginn: zahlOderNull(e["bestaetigte_zaehler_beginn"]),
      bestaetigteZaehlerEnde: zahlOderNull(e["bestaetigte_zaehler_ende"]),
      bestaetigteFotos: zahlOderNull(e["bestaetigte_fotos"]),
      nachBestaetigungGeaendertAm: txt(e["nach_bestaetigung_geaendert_am"]) || null,
    })),
    posten: alsListe(roh["posten"]).map((p): Einsatzposten => ({
      positionId: txt(p["position_id"]),
      position: txt(p["position"]),
      bedienungspflicht: txt(p["bedienungspflicht"]),
      bediener: alsListe(p["bediener"]).map((b) => ({
        belegungId: txt(b["belegung_id"]),
        personId: txt(b["person_id"]),
        name: txt(b["name"]),
      })),
    })),
    offenePunkte: alsListe(roh["offene_punkte"]).map((o): OffenerPunkt => ({
      art: txt(o["art"]) === "bediener_fehlt" ? "bediener_fehlt"
        : txt(o["art"]) === "fahrer_fehlt" ? "fahrer_fehlt"
          : txt(o["art"]) === "nach_bestaetigung_geaendert" ? "nach_bestaetigung_geaendert"
            : txt(o["art"]) === "frueher_frei" ? "frueher_frei" : "nicht_quittiert",
      positionId: txt(o["position_id"]),
      position: txt(o["position"]),
      tag: txt(o["tag"]) || null,
      grund: txt(o["grund"]) || null,
      ressource: txt(o["ressource"]) === "person" ? "person"
        : txt(o["ressource"]) === "maschine" ? "maschine" : null,
      name: txt(o["name"]),
      freiAb: txt(o["frei_ab"]) || null,
      verkauftBis: txt(o["verkauft_bis"]) || null,
    })),
  };
}

/* ══════════════ Die Haken ══════════════ */

export const zeitSchluessel = {
  /* Unter `handy`, damit ein Präfix den ganzen Tag des Fahrers auffrischt. */
  meinTag: ["handy", "einsatz"] as const,
  alle: ["zeiten"] as const,
  belegung: (id: string) => ["zeiten", "belegung", id] as const,
  vorgang: (nr: string) => ["zeiten", "vorgang", nr] as const,
};

/**
 * „Mein Einsatz heute" — über die Mappe (0113).
 *
 * Wer morgens auf dem Hof lädt und dann in die Kiesgrube fährt, hat seine
 * Einsätze auf dem Gerät. Ohne sie gäbe es dort nichts zum Einstempeln —
 * und genau das ist der Fall, der Offline am dringendsten braucht.
 */
export const useMeinTag = () =>
  useQuery({ queryKey: zeitSchluessel.meinTag, queryFn: () => mitMappe("handy/einsatz", meinTag) });

export const useZeitenDerBelegung = (belegungId: string) =>
  useQuery({
    queryKey: zeitSchluessel.belegung(belegungId),
    queryFn: () => zeitenDerBelegung(belegungId),
    enabled: !!belegungId,
  });

export const useZeitenDesVorgangs = (nr: string) =>
  useQuery({ queryKey: zeitSchluessel.vorgang(nr), queryFn: () => zeitenDesVorgangs(nr) });

/** Nach jedem Schreiben: der Tag des Fahrers, die Listen und die Marken im Brett. */
function useNachSchreiben() {
  const kunde = useQueryClient();
  return () => {
    void kunde.invalidateQueries({ queryKey: zeitSchluessel.meinTag });
    void kunde.invalidateQueries({ queryKey: zeitSchluessel.alle });
    laufendeNachladen();
  };
}

/* ══════════════ Stempeln, auch ohne Netz (0113) ══════════════

   **Mit Netz gilt `now()` der Datenbank.** Das Telefon schickt dann keinen
   Zeitpunkt; die Uhr eines Geräts stellt jeder nach, und an dieser Zahl
   hängt die Rechnung des Kunden.

   **Ohne Netz kommt die Uhr des Geräts mit** — die Wahl steht zwischen ihr
   und gar keiner Erfassung. Sie wird am Server geprüft und ist als
   `nachgetragen`/`offline` gekennzeichnet; die Disposition sieht es im
   Brett und am Auftrag.

   **Die Schicht über Mitternacht.** Wer im Funkloch einstempelt und dort
   auch ausstempelt, hat den Beginn noch im Gerät liegen. Dann wird kein
   zweiter Eintrag eingereiht, sondern dem ersten sein Ende gegeben:
   ein Eintrag mit Beginn und Ende, der hinausgeht, wenn Netz da ist.
   Das ist der Fall, den das Handy bisher gar nicht konnte. */

export function useEinstempeln() {
  const danach = useNachSchreiben();
  return useMutation({
    mutationFn: ({ belegungId, betreff }: { belegungId: string; betreff: string }) =>
      sendeOderEinreihen({
        art: "einstempeln",
        weg: "/handy/einsatz/einstempeln",
        betreff,
        nutzlast: { belegung_id: belegungId },
        zeitfeld: "zeitpunkt",
      }),
    onSuccess: danach,
    // Auch der Fehler frischt auf: „läuft schon" heißt, die Maske war alt.
    onError: danach,
  });
}

export function useAusstempeln() {
  const danach = useNachSchreiben();
  return useMutation({
    mutationFn: async ({ eintragId, wartetSeit, betreff }: {
      /** Der Eintrag auf dem Server — leer, wenn der Beginn noch im Gerät liegt. */
      eintragId: string | null;
      /** Die Eintragung in der Warteschlange, deren Ende hier gesetzt wird. */
      wartetSeit?: string;
      betreff: string;
    }) => {
      if (wartetSeit) {
        await endeNachtragen(wartetSeit, new Date());
        return { eingereiht: true, antwort: null };
      }
      return sendeOderEinreihen({
        art: "ausstempeln",
        weg: "/handy/einsatz/ausstempeln",
        betreff,
        nutzlast: { eintrag_id: eintragId },
        zeitfeld: "zeitpunkt",
      });
    },
    onSuccess: danach,
    onError: danach,
  });
}

/* ══════════════ Quittieren (0117) ══════════════

   Der Kunde bestätigt die Stunden **eines Einsatztags** — Name und, wenn
   jemand da ist, Unterschrift. Derselbe Weg wie das Protokoll: Mit Netz geht
   es gleich hinaus, ohne Netz liegt es samt Bild auf dem Gerät. Ein zweites
   Mal unterschreibt der Polier nicht, er ist weg.

   **Der Zeitpunkt kommt aus der Datenbank** (`assignment.signed_at`, Riegel in
   0117) und nicht aus der Uhr des Geräts: Eine Uhrzeit, die der Aufrufer
   mitschickt, ist keine Beglaubigung. Deshalb steht hier kein `zeitfeld`. */

export function useQuittieren() {
  const danach = useNachSchreiben();
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: (e: {
      belegungId: string;
      /** Leer heißt heute — der Fall am Handy. */
      tag?: string;
      unterschriebenVon: string;
      unterschriftUrl?: string;
      /** Das gezeichnete Bild, wenn es noch nicht abgelegt werden konnte. */
      unterschriftBlob?: Blob | null;
      betreff: string;
    }) => sendeOderEinreihen({
      art: "quittung",
      weg: "/zeiten/quittung",
      betreff: e.betreff,
      nutzlast: {
        belegung_id: e.belegungId,
        ...(e.tag ? { tag: e.tag } : {}),
        unterschrieben_von: e.unterschriebenVon,
        ...(e.unterschriftUrl ? { unterschrift_url: e.unterschriftUrl } : {}),
      },
      ...(e.unterschriftBlob
        ? { dateien: [{ feld: "unterschrift_url" as const, zweck: "unterschrift" as const, blob: e.unterschriftBlob }] }
        : {}),
    }),
    onSuccess: () => {
      danach();
      // Die Quittung des letzten Tages beendet den Einsatz: Tagesliste,
      // Startseite und der Vorgang selbst sind danach alt.
      void kunde.invalidateQueries();
    },
    onError: danach,
  });
}

/** Was an einer Belegung noch im Gerät liegt — für die Karte am Handy. */
export type Stempelstand = {
  /** Ein Einstempeln, das noch wartet. `ende` gesetzt heißt: schon beendet. */
  ein: Wartender | null;
  /** Ein Ausstempeln, das noch wartet. */
  aus: Wartender | null;
};

export function useStempelstand(belegungId: string, laeuftId: string | null): Stempelstand {
  const wartende = useWarteschlange();
  return {
    ein: wartende.find((w) => w.art === "einstempeln" && w.nutzlast["belegung_id"] === belegungId) ?? null,
    aus: laeuftId
      ? wartende.find((w) => w.art === "ausstempeln" && w.nutzlast["eintrag_id"] === laeuftId) ?? null
      : null,
  };
}

/* ══════════════ Korrigieren und nachtragen ══════════════

   Luca, 18.09.2026: *„Wenn ich jetzt auf Anfahrt geklickt hab oder ich hab's
   vergessen und bin da, dann will ich auch die Möglichkeit haben, das
   nachzutragen, mit Grund … weil man hier aktuell keine Möglichkeit hat."*

   **Beides geht seit 0126 durch die Warteschlange** — dieselbe Form wie jeder
   andere Schritt des Einsatzes. Am Schreibtisch mit Netz ändert das nichts: Der
   Ruf geht sofort hinaus, ein fachliches Nein kommt wie bisher zurück. Auf der
   Baustelle ohne Empfang liegt die Änderung auf dem Gerät und geht hinaus,
   sobald es wieder geht — sonst wäre genau der Ort, an dem die falsche Zeit
   auffällt, der einzige, an dem sie sich nicht richten lässt.

   **Der Pflichtgrund und die Spur sind dieselben** (`dienste/einsatzzeit.ts`).
   Es gibt einen Weg, eine Zeit zu ändern, und er führt über einen Grund. */

export function useZeitKorrigieren() {
  const danach = useNachSchreiben();
  return useMutation({
    mutationFn: (e: {
      id: string; grund: string; beginn?: Date; ende?: Date;
      /** Was im Warteband dasteht: „KR-01 · Bauer Hochbau". */
      betreff?: string;
    }) => sendeOderEinreihen({
      art: "zeitkorrektur",
      weg: "/zeiten/korrektur",
      betreff: e.betreff ?? "",
      nutzlast: {
        eintrag_id: e.id,
        grund: e.grund,
        ...(e.beginn ? { beginn: e.beginn.toISOString() } : {}),
        ...(e.ende ? { ende: e.ende.toISOString() } : {}),
      },
      zeitfeld: "erfasst_am",
    }),
    onSuccess: danach,
  });
}

export function useZeitNachtragen() {
  const danach = useNachSchreiben();
  return useMutation({
    mutationFn: (e: {
      belegungId: string; beginn: Date; ende: Date; grund: string; betreff?: string;
    }) => sendeOderEinreihen({
      art: "zeitnachtrag",
      weg: "/zeiten",
      betreff: e.betreff ?? "",
      nutzlast: {
        belegung_id: e.belegungId,
        beginn: e.beginn.toISOString(),
        ende: e.ende.toISOString(),
        grund: e.grund,
      },
      zeitfeld: "erfasst_am",
    }),
    onSuccess: danach,
  });
}

/* ══════════════ Was im Brett läuft ══════════════
   Eine Uhr für das ganze Brett, nicht eine je Balken. Das Brett zeichnet
   hundert Balken; hinge an jedem ein eigenes Nachladen, liefe es hundertmal
   versetzt. Hier holt **eine** Schleife jede Minute `GET /zeiten/laufend`,
   und **ein** Takt alle 30 Sekunden zählt die laufende Zeit im Browser
   hoch. Wer zuhört, bekommt beides; hört niemand mehr zu, stehen beide. */

export type LaufendeZeit = {
  id: string;
  belegungId: string;
  /** Zeitpunkt in Millisekunden. */
  seit: number;
  /** Von einem früheren Tag — vergessen auszustempeln. */
  vortag: boolean;
  /** Heute an dieser Belegung schon abgeschlossen. */
  sekundenHeute: number;
};

type Stand = { je: ReadonlyMap<string, LaufendeZeit>; jetzt: number };

let stand: Stand = { je: new Map(), jetzt: Date.now() };
const hoerer = new Set<() => void>();
let holUhr: number | undefined;
let taktUhr: number | undefined;

function melden(): void {
  hoerer.forEach((h) => h());
}

/** Holt, was gerade läuft. Scheitert es, bleibt der letzte Stand stehen. */
export async function laufendeNachladen(): Promise<void> {
  if (ATTRAPPE || hoerer.size === 0) return;
  try {
    const roh = await hole<Roh>("/zeiten/laufend");
    const je = new Map<string, LaufendeZeit>();
    for (const z of alsListe(roh["zeilen"])) {
      const seit = new Date(txt(z["seit"])).getTime();
      if (!Number.isFinite(seit)) continue;
      je.set(txt(z["belegung_id"]), {
        id: txt(z["id"]),
        belegungId: txt(z["belegung_id"]),
        seit,
        vortag: z["vortag"] === true,
        sekundenHeute: zahl(z["sekunden_heute"]),
      });
    }
    stand = { je, jetzt: Date.now() };
    melden();
  } catch {
    // Ein Brett ohne Marke ist kein Fehler, den man melden müsste: Der
    // letzte Stand bleibt, die nächste Minute fragt wieder. Eine
    // abgelaufene Sitzung meldet der Klient selbst.
  }
}

/** Wann zuletzt geholt wurde — ein Brett, das beim Blättern seine Balken
 *  neu zeichnet, meldet sich ab und gleich wieder an; das ist keine neue
 *  Frage an den Server (T-45: `/planung` holte `/zeiten/laufend` doppelt). */
let zuletztGeholt = 0;

function anmelden(hoer: () => void): () => void {
  hoerer.add(hoer);
  if (hoerer.size === 1) {
    if (Date.now() - zuletztGeholt > 5_000) {
      zuletztGeholt = Date.now();
      void laufendeNachladen();
    }
    holUhr = window.setInterval(() => void laufendeNachladen(), 60_000);
    taktUhr = window.setInterval(() => {
      stand = { ...stand, jetzt: Date.now() };
      melden();
    }, 30_000);
  }
  return () => {
    hoerer.delete(hoer);
    if (hoerer.size === 0) {
      window.clearInterval(holUhr);
      window.clearInterval(taktUhr);
    }
  };
}

/** Die laufende Zeit an einer Belegung — `null`, wenn dort nichts läuft. */
export function useLaufendeZeit(belegungId: string): { zeit: LaufendeZeit; jetzt: number } | null {
  const aktuell = useSyncExternalStore(anmelden, () => stand);
  const zeit = aktuell.je.get(belegungId);
  return zeit ? { zeit, jetzt: aktuell.jetzt } : null;
}

/* ══════════════ Zahlen und Uhrzeiten ══════════════ */

/** `07:12` in deutscher Zeit (N8-02). */
export function uhr(iso: string | number): string {
  return uhrImBetrieb(typeof iso === "number" ? new Date(iso) : iso);
}

/** `16.09.` aus einem Kalendertag `JJJJ-MM-TT`. */
export function tagKurz(tag: string): string {
  return tagKurzImBetrieb(tag);
}

/** `16.09.` aus einem Zeitpunkt — in deutscher Zeit, nicht UTC (N8-02). */
export function datumKurz(iso: string | number): string {
  return tagKurzImBetrieb(typeof iso === "number" ? new Date(iso) : iso);
}

/**
 * Stunden als Zahl mit Komma: `3,5`. Die Einheit steht im Text
 * (`einsatzzeit.stunden`), nicht hier. Viertelstunden werden nicht gebildet —
 * eine Taktung wäre eine Preisregel (04-preisfindung.md).
 */
export function stundenZahl(sekunden: number): string {
  const stunden = Math.max(0, sekunden) / 3600;
  return stunden.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

/** Sekunden eines Eintrags — ein laufender zählt bis `jetzt`. */
export function sekundenVon(e: { beginn: string; ende: string | null }, jetzt: number): number {
  const von = new Date(e.beginn).getTime();
  const bis = e.ende ? new Date(e.ende).getTime() : jetzt;
  return Number.isFinite(von) && Number.isFinite(bis) ? Math.max(0, (bis - von) / 1000) : 0;
}

/**
 * Der i18n-Pfad eines Fehlers der Zeiterfassung.
 *
 * `zeit.laeuft_schon` → `einsatzzeit.fehler.laeuft_schon`; alles andere geht
 * den allgemeinen Weg (`Fehler.textPfad`).
 */
export function zeitFehlerPfad(e: unknown): string {
  if (e instanceof Fehler) {
    if (e.schluessel.startsWith("zeit.")) return `einsatzzeit.fehler.${e.schluessel.slice(5)}`;
    return e.textPfad;
  }
  return "fehler.unbekannt";
}
